import Vue from 'vue';
import Vuex from 'vuex';

import { event } from 'vue-gtag';

import api from './api';
import pathify from './pathify';
import { make } from 'vuex-pathify';
import moment from 'moment';
import { compact, filter, map, omitBy, range, uniq } from 'lodash';

import { statuses, sectors, functionalAreas, types } from '@/common';

import { makeJsonEncoder, makeJsonDecoder } from '@urlpack/json';

const encoder = makeJsonEncoder();
const decoder = makeJsonDecoder();

Vue.use(Vuex);

const queryParams = (url) => {
    const index = url.indexOf('?');
    if (index > -1) {
        return url.substring(index + 1);
    }
    return url;
};

const state = {
    loading: 0,
    summary: {},
    featured: [],
    standards: [],
    sdos: [],
    priorities: [],
    selectedPriority: 'High urgency/high impact',
    filters: {
        show: true,
        hex: '',
        text: '',
        fda: false,
        bundles: false,
        feature: '',
        selectedStatuses: [],
        selectedSectors: [],
        selectedFunctionalAreas: [],
        selectedTypes: [],
        selectedSdos: []
    },
    common: {
        statuses: statuses,
        sectors: sectors,
        functionalAreas: functionalAreas,
        types: types
    }
};

const getters = {
    ...make.getters(state),
    availableByYear: (state) => (sector) => {
        let years = [];
        const yearCounts = {};
        state.standards.forEach((standard) => {
            if (standard.sectors.includes(sector) && standard.availableYear) {
                years.push(standard.availableYear);
                if (!yearCounts[standard.availableYear]) {
                    yearCounts[standard.availableYear] = 0;
                }
                yearCounts[standard.availableYear]++;
            }
        });
        years = uniq(years).sort();
        years = range(2000, parseInt(years[years.length - 1]) + 1).map((year) => `${year}`);
        const counts = years.map((year) => yearCounts[year] || 0);
        return { years: years, counts: counts };
    },
    countStandards(state) {
        return filter(state.standards, (standard) => standard.status !== 'Area of Need').length;
    },
    countOrganizations(state) {
        return compact(uniq(map(state.standards, (standard) => standard.sdo))).length;
    },
    countAvailable(state) {
        return filter(state.standards, { status: 'Available' }).length;
    },
    countInDevelopment(state) {
        return filter(state.standards, { status: 'In Development' }).length;
    },
    countAreasOfNeed(state) {
        return filter(state.standards, { status: 'Area of Need' }).length;
    },
    featuredNew(state) {
        return filter(state.featured, { feature: 'new' });
    },
    featuredAvailable(state) {
        return filter(state.featured, { feature: 'available' });
    },
    featuredRevised(state) {
        return filter(state.featured, { feature: 'revised' });
    },
    featuredRecent(state) {
        return filter(state.featured, { feature: 'recent' });
    },
    selectedPriorityStandards(state) {
        return filter(state.priorities, { priority: state.selectedPriority });
    },
    totals(state, getters) {
        return {
            standards: getters.countStandards,
            organizations: getters.countOrganizations,
            available: getters.countAvailable,
            inDevelopment: getters.countInDevelopment,
            areasOfNeed: getters.countAreasOfNeed
        };
    },
    params(state) {
        return omitBy({
            hex: state.filters.hex,
            text: state.filters.text,
            fda: state.filters.fda,
            bundles: state.filters.bundles,
            feature: state.filters.feature,
            status: state.filters.selectedStatuses,
            sectors: state.filters.selectedSectors,
            functionalAreas: state.filters.selectedFunctionalAreas,
            type: state.filters.selectedTypes,
            sdo: state.filters.selectedSdos
        }, (value) => {
            if (Array.isArray(value)) {
                return 0 === value.length;
            }
            return !value;
        });
    },
    encodedParams(state, getters) {
        return encoder.encode(getters.params);
    },
    searchPath(state, getters) {
        return `/search/${getters.encodedParams}`;
    },
    shareLink(state, getters) {
        return `${window.location.origin}${getters.searchPath}`;
    },
    hasActiveFilters(state) {
        return state.filters.hex?.length || state.filters.text?.length || state.filters.fda || state.filters.bundles || state.filters.feature || state.filters.selectedStatuses.length || state.filters.selectedSectors.length || state.filters.selectedFunctionalAreas.length || state.filters.selectedTypes.length || state.filters.selectedSdos.length;
    }
};
const mutations = {
    ...make.mutations(state),
    setHexFilter(state, hex) {
        state.filters.hex = hex;
    },
    setFilters(state, params) {
        state.filters.hex = params.hex || '';
        state.filters.text = params.text || '';
        state.filters.fda = params.fda || false;
        state.filters.bundles = params.bundles || false;
        state.filters.feature = params.feature || '';
        state.filters.selectedStatuses = params.status || [];
        state.filters.selectedSectors = params.sectors || [];
        state.filters.selectedFunctionalAreas = params.functionalAreas || [];
        state.filters.selectedTypes = params.type || [];
        state.filters.selectedSdos = params.sdo || [];
    },
    resetFilters(state, show = false) {
        if (show) {
            state.filters.show = true;
        }
        state.filters.hex = '';
        state.filters.text = '';
        state.filters.fda = false;
        state.filters.bundles = false;
        state.filters.feature = '';
        state.filters.selectedStatuses = [];
        state.filters.selectedSectors = [];
        state.filters.selectedFunctionalAreas = [];
        state.filters.selectedTypes = [];
        state.filters.selectedSdos = [];
    },
    incrementLoading(state) {
        state.loading++;
    },
    decrementLoading(state) {
        state.loading--;
    }
};
const actions = {
    ...make.actions(state),
    init({ dispatch }) {
        return Promise.all([dispatch('lookupSDOs'), dispatch('featured'), dispatch('priorities'), dispatch('summary'), dispatch('search')]);
    },
    permalink({ commit, dispatch }, permalink) {
        commit('resetFilters', true);
        try {
            const decodedParams = decoder.decode(permalink);
            commit('setFilters', decodedParams);
        } catch (error) {
            // permalink is invalid, reset filters
            commit('resetFilters', true);
        }
        dispatch('search');
    },
    featured({ commit }) {
        commit('incrementLoading');
        return api
            .get('/featured')
            .then((response) => {
                commit(
                    'featured',
                    map(response.data, (standard) => ({ ...standard, isExpanded: false }))
                );
            })
            .finally(() => {
                commit('decrementLoading');
            });
    },
    priorities({ commit }) {
        commit('incrementLoading');
        return api
            .get('/search', { params: { status: 'Area of Need' } })
            .then((response) => {
                commit(
                    'priorities',
                    map(response.data, (standard) => ({ ...standard, isExpanded: false }))
                );
            })
            .finally(() => {
                commit('decrementLoading');
            });
    },
    search({ commit, getters }) {
        const params = getters.params;
        commit('incrementLoading');
        return api
            .get('/search', { params })
            .then((response) => {
                event('query', { event_category: 'Search', event_label: queryParams(api.getUri({ url: '/search', params })), value: response.data.length });
                const isExpanded = /expand/i.test(queryParams(window.location.href));
                commit(
                    'standards',
                    map(response.data, (standard) => ({ ...standard, isExpanded }))
                );
            })
            .finally(() => {
                commit('decrementLoading');
            });
    },
    summary({ commit }) {
        commit('incrementLoading');
        return api
            .get('/summary')
            .then((response) => {
                commit('summary', response.data);
            })
            .finally(() => {
                commit('decrementLoading');
            });
    },
    lookupSDOs({ commit }) {
        commit('incrementLoading');
        return api
            .get('/lookup', { params: { field: 'sdo' } })
            .then((response) => {
                commit('sdos', response.data);
            })
            .finally(() => {
                commit('decrementLoading');
            });
    },
    export({ commit }, { standards, params, totals }) {
        const url = window.location.origin;
        commit('incrementLoading');
        return api
            .post('/export', { standards, params, totals, url }, { responseType: 'blob' })
            .then((response) => {
                if (params) {
                    event('results', { event_category: 'Export', event_label: `Count: ${standards.length}` });
                } else {
                    event('single', { event_category: 'Export', event_label: standards[0].title });
                }
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `RegenMed_Portal_${moment().format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS).replace(/:/g, '_')}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            })
            .finally(() => {
                commit('decrementLoading');
            });
    }
};

export default new Vuex.Store({
    state,
    getters,
    mutations,
    actions,
    plugins: [pathify.plugin]
});
